import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, balanceEntries } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { useActions } from "../components/ActionDialogs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Plus, Search, Building2, User } from "lucide-react";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const { open } = useActions();
  const navigate = useNavigate();

  const load = useCallback((query = "") => {
    setLoading(true);
    api.get(`/clients${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then(({ data }) => setClients(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  useEffect(() => {
    const r = () => load(q);
    window.addEventListener("shb:refresh", r);
    return () => window.removeEventListener("shb:refresh", r);
  }, [q, load]);

  return (
    <div className="space-y-5 animate-in-up">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">Müvekkiller</h1>
        <Button data-testid="add-client-btn" onClick={() => open("client", { onDone: () => load(q) })}
          className="rounded-xl bg-navy-900 hover:bg-navy-800 text-white active:scale-[0.98] transition-transform">
          <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Yeni Müvekkil</span>
        </Button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Ad, numara veya açıklama ile ara…" className="pl-10 h-12 rounded-xl bg-white" />
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400">Yükleniyor…</div>
      ) : clients.length === 0 ? (
        <div className="py-16 text-center text-slate-400">Müvekkil bulunamadı.</div>
      ) : (
        <div className="grid gap-3">
          {clients.map((c) => (
            <button key={c.id} data-testid={`client-card-${c.id}`} onClick={() => navigate(`/muvekkiller/${c.id}`)}
              className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4 text-left hover:-translate-y-0.5 hover:shadow-md transition-all">
              <span className="w-11 h-11 rounded-xl bg-navy-900 text-white flex items-center justify-center shrink-0">
                {c.client_type === "corporate" ? <Building2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-navy-900 truncate">{c.name}</p>
                  <StatusBadge status={c.advance_status} />
                </div>
                <p className="text-xs text-slate-400 font-mono">{c.client_no}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Avans</p>
                <div className="text-sm text-navy-900 font-mono font-semibold">
                  {balanceEntries(c.balances).map(([cur, v]) => <div key={cur}>{fmtMoney(v, cur)}</div>)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
